// Types
import { IWidgetErrorLevel, IWidgetStage, IWidgetType } from "./types";

const LAST_PROCESSING_TIMEOUT = 3000;
const DOMAIN_CATCHER_TIMEOUT = 1000;
const MAX_COMMON_ERRORS = 3;
const FIRST_STEPS_LOCALSTORAGE = "frb.cather.init";
const DOMAIN_CATCHER_LOCALSTORAGE = "frb.cather";
const ERRORS = {
  INIT: "INIT",
  MEDIA: "MEDIA",
  BUNDLE: "BUNDLE",
  FETCH: "FETCH",
};

interface FrisbuyCommonError {
  type: IWidgetStage;
  widgetType: IWidgetType;
  location?: string;
  domain?: string;
  request?: string;
  browser?: string;
  level?: IWidgetErrorLevel;
  method?: string;
  data?: { [key: string]: any };
  error?: { [key: string]: any } | string | null | undefined;
  statusCode?: number;
  loadTime?: number;
  timestamp?: number;
}

interface FrisbuyStorageError {
  isSended: boolean;
  error: FrisbuyCommonError;
}

export default class ErrorHandler {
  intervalId: number | null = null;

  queue: Array<FrisbuyStorageError> = [];

  stepsToForceSend = 3;
  lastProcessingTime = Date.now();

  constructor() {
    if (!window.frisbuy) window.frisbuy = {};

    if (!window.frisbuy.errorHandler) window.frisbuy.errorHandler = {};

    this.init();
  }

  init() {
    // Создаем debounce функцию для добавления ошибок в очередь
    window.frisbuy.errorHandler.addErrorToQueue = (
      error: FrisbuyCommonError
    ) => {
      if (this.intervalId) clearTimeout(this.intervalId);

      if (!error.timestamp) {
        error.timestamp = Date.now();
      }

      this._saveError(error);

      if (Date.now() - this.lastProcessingTime > LAST_PROCESSING_TIMEOUT) {
        this.lastProcessingTime = Date.now();

        this._queueProcessing();
      } else {
        this.intervalId = window.setTimeout(() => {
          this.lastProcessingTime = Date.now();

          this._queueProcessing();
        }, DOMAIN_CATCHER_TIMEOUT);
      }
    };

    // TODO обработка ошибок с предыдущих этапов FIRST_STEPS_LOCALSTORAGE
    // localStorage.removeItem(FIRST_STEPS_LOCALSTORAGE);

    // Загружаем очередь из локального хранилища для обработки ошибок
    this.queue = JSON.parse(
      localStorage.getItem(DOMAIN_CATCHER_LOCALSTORAGE) || "[]"
    );

    // Фильтруем устаревшие ошибки из очереди
    this._filterOutdatedErrors();

    // Запускаем обработку очереди
    this._queueProcessing();
  }

  _saveError(error: FrisbuyCommonError) {
    // Убираем дубликаты по url
    if (this.queue.find((e) => e.error.request === error.request)) return;

    // Ограничиваем количество одинаковых ошибок по типам MEDIA, FETCH, BUNDLE
    if (
      this.queue.filter(
        (data: FrisbuyStorageError) =>
          [ERRORS.MEDIA, ERRORS.FETCH, ERRORS.BUNDLE].indexOf(
            data.error.type
          ) >= 0
      ).length >= MAX_COMMON_ERRORS
    )
      return;

    this.queue.push({ error, isSended: false });

    localStorage.setItem(
      DOMAIN_CATCHER_LOCALSTORAGE,
      JSON.stringify(this.queue)
    );
  }

  private _filterOutdatedErrors() {
    const fiftenMin = 15 * 60 * 1000;
    const thirtyMin = 30 * 60 * 1000;
    const tenMin = 10 * 60 * 1000;
    const three_days = 3 * 24 * 60 * 60 * 1000;

    this.queue = this.queue.filter((data) => {
      if (!data.error.timestamp) return false;
      if (Date.now() - data.error.timestamp > three_days) return false;
      if (!data.isSended) return true;

      switch (data.error.type) {
        case ERRORS.INIT:
          return Date.now() - data.error.timestamp < fiftenMin;
        case ERRORS.MEDIA:
          return Date.now() - data.error.timestamp < thirtyMin;
        case ERRORS.BUNDLE:
          return Date.now() - data.error.timestamp < thirtyMin;
        case ERRORS.FETCH:
          return Date.now() - data.error.timestamp < tenMin;
        default:
          return false;
      }
    });

    localStorage.setItem(
      DOMAIN_CATCHER_LOCALSTORAGE,
      JSON.stringify(this.queue)
    );
  }

  private _setSendedByIndex(index: number) {
    this.queue[index].isSended = true;

    localStorage.setItem(
      DOMAIN_CATCHER_LOCALSTORAGE,
      JSON.stringify(this.queue)
    );
  }

  private _queueProcessing() {
    this.queue.forEach((error, index) => {
      if (error.isSended) return;

      new Promise((resolve) => {
        // TODO send to server
        // console.log("Sending error to server:", error);
        resolve(true);
      }).then(() => {
        this._setSendedByIndex(index);
      });
    });
  }
}
